/* ============================================================
   Pholoh back-in-stock popup.
   Opens from the PDP "Size not in stock?" link, lists the sold-out
   sizes, and subscribes the shopper via Klaviyo's onsite Back-in-Stock
   endpoint so they get an email when their size restocks.

   Klaviyo public key resolution order:
     1. data-klaviyo-key on the modal (theme setting klaviyo_public_key)
     2. company_id from the loaded Klaviyo onsite script
     3. window.klaviyo / window._klOnsite company id
   ============================================================ */
(function () {
  'use strict';

  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var lastFocus = null;

  function klaviyoKey(modal) {
    var explicit = (modal.getAttribute('data-klaviyo-key') || '').trim();
    if (explicit) return explicit;
    var s = document.querySelector('script[src*="klaviyo.js"], script[src*="klaviyo.com/onsite"]');
    if (s && s.src) {
      var m = s.src.match(/company_id=([^&]+)/);
      if (m) return decodeURIComponent(m[1]);
    }
    if (window.klaviyo && window.klaviyo.company_id) return window.klaviyo.company_id;
    if (window._klOnsite && window._klOnsite.company_id) return window._klOnsite.company_id;
    return '';
  }

  function findModal(btn) {
    /* Grid cards (2026-09-14): many sold-out cards share one section, so "first modal in the
       section" would open the wrong product. Cards name their own modal by id. */
    var target = btn.getAttribute('data-pholoh-bis-target');
    if (target) return document.getElementById(target);
    var sec = btn.closest('[data-section-id]');
    return (sec && sec.querySelector('[data-pholoh-bis]')) || document.querySelector('[data-pholoh-bis]');
  }

  function open(modal) {
    lastFocus = document.activeElement;
    /* Grids re-rendered by filtering/pagination arrive after init(); move on open too, so a
       transformed card never traps position:fixed. */
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    // force reflow so the CSS transition runs
    void modal.offsetWidth;
    modal.classList.add('is-open');
    document.documentElement.classList.add('pholoh-bis-lock');
    // preselect when there's only one sold-out size
    if (modal.dataset.single === '1') {
      var only = modal.querySelector('.pholoh-bis__size');
      if (only) selectSize(only);
    }
    var focusTarget = modal.querySelector('.pholoh-bis__size.is-selected') ||
      modal.querySelector('.pholoh-bis__size') || modal.querySelector('[data-pholoh-bis-close]');
    if (focusTarget) focusTarget.focus();
  }

  function close(modal) {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('pholoh-bis-lock');
    var done = function () {
      modal.hidden = true;
      modal.removeEventListener('transitionend', done);
    };
    modal.addEventListener('transitionend', done);
    // fallback if no transition fires
    setTimeout(function () { if (!modal.classList.contains('is-open')) modal.hidden = true; }, 350);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function selectSize(btn) {
    var modal = btn.closest('[data-pholoh-bis]');
    if (!modal) return;
    modal.querySelectorAll('.pholoh-bis__size').forEach(function (b) {
      b.classList.toggle('is-selected', b === btn);
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });
    modal.dataset.variant = btn.getAttribute('data-bis-variant') || '';
    modal.classList.add('is-ready');
    var msg = modal.querySelector('[data-pholoh-bis-msg]');
    if (msg) msg.textContent = '';
    var field = modal.querySelector('[data-pholoh-bis-phone]') || modal.querySelector('[data-pholoh-bis-email]');
    if (field && !field.value) field.focus();
  }

  /* US-first phone normalisation to E.164. Returns '' when it cannot be trusted. */
  function toE164(raw) {
    var s = (raw || '').trim();
    if (!s) return '';
    var plus = s.charAt(0) === '+';
    var d = s.replace(/\D/g, '');
    if (plus) return (d.length >= 10 && d.length <= 15) ? '+' + d : '';
    if (d.length === 10) return '+1' + d;
    if (d.length === 11 && d.charAt(0) === '1') return '+' + d;
    return '';
  }

  var REVISION = '2025-01-15';

  function clientPost(path, key, payload) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(key), {
      method: 'POST',
      headers: { 'content-type': 'application/vnd.api+json', 'revision': REVISION },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (r.status === 202 || r.ok) return true;
      return r.json().catch(function () { return {}; }).then(function (d) {
        var e = d && d.errors && d.errors[0];
        throw new Error((e && (e.detail || e.title)) || ('HTTP ' + r.status));
      });
    });
  }

  /* Old onsite endpoint, email only. Kept as the fallback so a Klaviyo API change never loses a signup. */
  function legacyEmailOnly(key, email, variant) {
    var body = 'a=' + encodeURIComponent(key) + '&email=' + encodeURIComponent(email) +
      '&variant=' + encodeURIComponent(variant) + '&platform=shopify';
    return fetch('https://a.klaviyo.com/onsite/components/back-in-stock/subscribe', {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded', 'cache-control': 'no-cache' },
      body: body
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (d) {
        if (!(r.ok && d && d.success !== false)) throw new Error((d && d.errors && d.errors[0]) || 'failed');
        return true;
      });
    });
  }

  /* 2026-09-14 (Diego): PHONE ONLY. No email field, no email fallback. A valid number and a
     ticked consent box are both required; consent goes to list NOTIFY ME, the back-in-stock
     subscription is SMS only. */
  function subscribe(modal) {
    var variant = modal.dataset.variant;
    var phoneEl = modal.querySelector('[data-pholoh-bis-phone]');
    var smsBox = modal.querySelector('[data-pholoh-bis-sms]');
    var form = modal.querySelector('[data-pholoh-bis-form]');
    var msg = modal.querySelector('[data-pholoh-bis-msg]');
    var submit = modal.querySelector('[data-pholoh-bis-submit]');
    var phoneRaw = phoneEl ? phoneEl.value.trim() : '';
    /* 2026-10-14 (Diego): email is required too; the restock alert goes out by email AND text. */
    var emailEl = modal.querySelector('[data-pholoh-bis-email]');
    var email = emailEl ? emailEl.value.trim() : '';

    msg.className = 'pholoh-bis__msg';
    if (!variant) { msg.textContent = 'Pick a size first.'; return; }
    if (emailEl && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { msg.textContent = 'Enter a valid email.'; emailEl.focus(); return; }
    var phone = toE164(phoneRaw);
    if (!phone) { msg.textContent = 'Enter a valid phone number.'; if (phoneEl) phoneEl.focus(); return; }
    if (!(smsBox && smsBox.checked)) { msg.textContent = 'Tick the box so we can text you when it restocks.'; if (smsBox) smsBox.focus(); return; }

    var key = klaviyoKey(modal);
    if (!key) {
      msg.textContent = "Back in stock alerts aren't set up yet. Try again soon.";
      msg.classList.add('is-error');
      return;
    }

    submit.disabled = true;
    submit.classList.add('is-loading');
    msg.textContent = '';

    var listId = (form && form.getAttribute('data-sms-list')) || '';
    var consent = clientPost('subscriptions', key, {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in stock Notify me (' + location.pathname + ')',
          profile: { data: { type: 'profile', attributes: email ? {
            email: email,
            phone_number: phone,
            subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } }, sms: { marketing: { consent: 'SUBSCRIBED' } } }
          } : {
            phone_number: phone,
            subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
          } } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    });
    var bis = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: email ? ['EMAIL', 'SMS'] : ['SMS'],
          profile: { data: { type: 'profile', attributes: email ? { email: email, phone_number: phone } : { phone_number: phone } } }
        },
        relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variant } } }
      }
    };
    var sms = true;

    consent
      .then(function () { return clientPost('back-in-stock-subscriptions', key, bis); })
      .then(function () {
        modal.classList.add('is-done');
        msg.textContent = "You're on the list. We'll text and email you the moment it's back.";
        msg.classList.add('is-ok');
        track('notify_signup', modal, (modal.querySelector('.pholoh-bis__size.is-selected') || {}).textContent || '');
        try {
          document.dispatchEvent(new CustomEvent('pholoh:bis-subscribed', { detail: { variant: variant, sms: sms } }));
          if (window.klaviyo && typeof window.klaviyo.push === 'function') {
            window.klaviyo.push(['track', 'Pholoh Notify Me Clicked', { variant_id: variant, sms_opt_in: sms, page: location.pathname }]);
          }
        } catch (e) {}
      })
      .catch(function () {
        msg.textContent = 'Something went wrong. Please try again.';
        msg.classList.add('is-error');
        submit.disabled = false;
      })
      .then(function () { submit.classList.remove('is-loading'); });
  }

  /* ---- tracking: same /fit collector + session id as snippets/pholoh-page-clicks.liquid ---- */
  function track(kind, modal, variantLabel) {
    try {
      var sid = '';
      try {
        sid = sessionStorage.getItem('byfSid') || '';
        if (!sid) { sid = Date.now().toString(36) + Math.random().toString(36).slice(2, 10); sessionStorage.setItem('byfSid', sid); }
      } catch (e) { sid = 'x' + Math.random().toString(36).slice(2, 10); }
      var body = JSON.stringify({ events: [{
        sid: sid, surface: /\/products\//.test(location.pathname) ? 'product' : 'collection', kind: kind, seq: 0,
        device: (window.matchMedia && window.matchMedia('(max-width:768px)').matches) ? 'mobile' : 'desktop',
        category: (modal && modal.getAttribute('data-handle')) || '', item: (modal && modal.getAttribute('data-title')) || '',
        variant: String(variantLabel || '').trim().slice(0, 40), value: 0, dwell_ms: 0 }] });
      var EP = 'https://link.pholoh.us/fit/e';
      if (navigator.sendBeacon) navigator.sendBeacon(EP, new Blob([body], { type: 'text/plain' }));
      else fetch(EP, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true }).catch(function () {});
    } catch (e) {}
  }

  /* ---- product page (2026-09-14): our button replaces Add to Bag whenever the selected size is sold out ---- */
  function pdpSync(variant) {
    document.querySelectorAll('[data-pholoh-bis-pdp]').forEach(function (box) {
      var map = {};
      try { map = JSON.parse((box.querySelector('[data-pholoh-bis-avail]') || {}).textContent || '{}'); } catch (e) {}
      var vid = variant ? String(variant.id) : (box.getAttribute('data-current-variant') || '');
      var soldOut = variant ? !variant.available : map[vid] === 0;
      var btn = box.querySelector('[data-pholoh-bis-open]');
      var scope = box.closest('[data-product-blocks]') || box.closest('form') || document;
      var add = scope.querySelector('button[name="add"]');
      box.setAttribute('data-current-variant', vid);
      if (btn) { btn.hidden = !soldOut; btn.setAttribute('data-current-variant', vid); }
      if (add) add.classList.toggle('pholoh-bis-hide-add', soldOut);
    });
  }

  function init() {
    // Move each modal to <body> so position:fixed is never trapped by a
    // transformed/overflow ancestor in the product blocks.
    document.querySelectorAll('[data-pholoh-bis]').forEach(function (m) {
      if (m.parentElement !== document.body) document.body.appendChild(m);
    });

    pdpSync(null);
    document.addEventListener('variant:change', function (e) {
      if (e.detail && e.detail.variant) pdpSync(e.detail.variant);
    });

    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-pholoh-bis-open]');
      if (openBtn) {
        e.preventDefault();
        var m = findModal(openBtn);
        if (m) {
          open(m);
          var cv = openBtn.getAttribute('data-current-variant');
          var pick = cv && m.querySelector('.pholoh-bis__size[data-bis-variant="' + cv + '"]');
          if (pick) selectSize(pick);
          track('notify_open', m, pick ? pick.textContent : '');
        }
        return;
      }

      var closeBtn = e.target.closest('[data-pholoh-bis-close]');
      if (closeBtn) { var mc = closeBtn.closest('[data-pholoh-bis]'); if (mc) close(mc); return; }

      var sizeBtn = e.target.closest('.pholoh-bis__size');
      if (sizeBtn) { selectSize(sizeBtn); return; }
    });

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-pholoh-bis-form]');
      if (!form) return;
      e.preventDefault();
      var modal = form.closest('[data-pholoh-bis]');
      if (modal) subscribe(modal);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var openModal = document.querySelector('[data-pholoh-bis].is-open');
      if (openModal) close(openModal);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
